import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Truck, ShieldCheck, CreditCard, RefreshCw, Store } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BenefitsBar - Barra superior de beneficios
 * Desktop: Fija con todos los beneficios visibles
 * Mobile: Marquee animado
 */
const BenefitsBar = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [speed, setSpeed] = useState(50);

  // Beneficios estáticos con iconos
  const benefits = [
    { 
      text: "Envío gratis", 
      icon: Truck,
      subtext: "En pedidos +$1,197",
      mobileText: "Envío gratis +$1,197"
    },
    { 
      text: "Garantía de 30 días", 
      icon: RefreshCw,
      subtext: "Satisfacción garantizada",
      mobileText: "Garantía 30 días"
    },
    { 
      text: "Pago 100% Seguro", 
      icon: CreditCard,
      subtext: "Respaldado por Stripe",
      mobileText: "Pago Seguro"
    },
    { 
      text: "Pagos en OXXO", 
      icon: Store,
      subtext: "Mayor facilidad",
      mobileText: "Pagos en OXXO"
    },
  ];

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      const response = await fetch(`${API_URL}/api/announcements`);
      if (response.ok) {
        const data = await response.json();
        if (data.messages && data.messages.length > 0) {
          setAnnouncements(data.messages);
        }
        setSpeed(data.speed || 50);
      }
    } catch (error) {
      console.error('Error loading announcements:', error);
    }
  };

  // Para el marquee móvil, usar announcements del backend si existen, sino usar benefits
  const mobileItems = announcements.length > 0 
    ? announcements 
    : benefits.map(b => ({ text: b.mobileText, icon: "✨" }));

  const repeatedContent = [...mobileItems, ...mobileItems, ...mobileItems, ...mobileItems];
  const contentWidth = repeatedContent.length * 300;
  const duration = contentWidth / (speed * 5);

  const BAR_HEIGHT = 70; // Altura de la barra (más alta para que quepa el texto)

  return (
    <>
      {/* Desktop Version - Fija con todos los beneficios */}
      <div 
        className="hidden md:flex"
        style={{ 
          height: `${BAR_HEIGHT}px`,
          backgroundColor: '#0d0d1a', // Color sólido más oscuro para contraste
          position: 'relative',
          overflow: 'hidden',
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        }}
      >
        {/* Contenido centrado */}
        <div className="max-w-7xl mx-auto w-full flex items-center justify-center gap-8 lg:gap-16 px-4">
          {benefits.map((benefit, index) => {
            const IconComponent = benefit.icon;
            return (
              <div 
                key={index}
                className="flex items-center gap-2.5 text-white"
              >
                <IconComponent 
                  size={18} 
                  className="text-cyan-400 flex-shrink-0" 
                  strokeWidth={1.5}
                />
                <div className="flex flex-col leading-tight">
                  <span className="text-[13px] font-medium text-white">
                    {benefit.text}
                  </span>
                  {benefit.subtext && (
                    <span className="text-[10px] text-white/50">
                      {benefit.subtext}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Línea inferior sutil */}
        <div 
          style={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'rgba(0,255,255,0.1)',
          }}
        />
      </div>

      {/* Mobile Version - Marquee animado */}
      <div 
        className="md:hidden"
        style={{ 
          height: `${BAR_HEIGHT}px`,
          backgroundColor: '#0d0d1a',
          position: 'relative',
          overflow: 'hidden',
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        }}
      >
        {/* Contenedor del marquee */}
        <motion.div
          style={{ 
            display: 'inline-block',
            whiteSpace: 'nowrap',
            height: `${BAR_HEIGHT}px`,
            lineHeight: `${BAR_HEIGHT}px`
          }}
          animate={{ x: [0, -contentWidth / 4] }}
          transition={{
            x: {
              duration: duration,
              repeat: Infinity,
              ease: 'linear',
            },
          }}
        >
          {repeatedContent.map((item, index) => (
            <span
              key={index}
              style={{
                display: 'inline',
                marginLeft: '30px',
                marginRight: '30px',
                height: `${BAR_HEIGHT}px`,
                lineHeight: `${BAR_HEIGHT}px`,
                verticalAlign: 'middle'
              }}
            >
              {/* Icono */}
              <span 
                style={{ 
                  fontSize: '14px',
                  marginRight: '8px',
                  verticalAlign: 'middle',
                }}
              >
                {typeof item.icon === 'string' ? item.icon : '✨'}
              </span>
              
              {/* Texto */}
              <span 
                style={{ 
                  fontSize: '12px',
                  fontWeight: 500,
                  verticalAlign: 'middle',
                  color: 'white'
                }}
              >
                {item.text}
              </span>

              {/* Separador */}
              <span 
                style={{ 
                  marginLeft: '30px',
                  color: 'rgba(255,255,255,0.3)',
                  fontSize: '8px',
                  verticalAlign: 'middle'
                }}
              >
                •
              </span>
            </span>
          ))}
        </motion.div>

        {/* Gradiente izquierdo */}
        <div 
          style={{ 
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: '40px',
            background: 'linear-gradient(to right, #0d0d1a, transparent)',
            pointerEvents: 'none',
            zIndex: 10
          }}
        />
        
        {/* Gradiente derecho */}
        <div 
          style={{ 
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            width: '40px',
            background: 'linear-gradient(to left, #0d0d1a, transparent)',
            pointerEvents: 'none',
            zIndex: 10
          }}
        />

        {/* Línea inferior sutil */}
        <div 
          style={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'rgba(255,255,255,0.1)',
          }}
        />
      </div>
    </>
  );
};

export default BenefitsBar;
